import { matchReaderRouteToken } from '@bangle.io/constants';
import {
  CoreServiceProvider,
  LoggerProvider,
  type Services,
  useCoreServices,
} from '@bangle.io/context';
import type { Logger } from '@bangle.io/logger';
import { OmniSearch } from '@bangle.io/omni-search';
import type { AppRouteInfo, RootEmitter, Store } from '@bangle.io/types';
import { Toaster } from '@bangle.io/ui-components';
import { Provider, useAtomValue } from 'jotai/react';
import React from 'react';
import { AppErrorHandler } from './app-error-handler';
import { pocketBaseResetTokenFromHash } from './auth/auth-recovery';
import { AuthCallbackPage } from './auth/auth-callback-page';
import { JoinPage } from './auth/join-page';
import { LoginPage } from './auth/login-page';
import {
  initializePwaInstallPromptTracking,
  syncAppDocumentTitle,
  syncInstalledDisplayMode,
  syncWindowControlsOverlayState,
} from './common/pwa-install';
import { PwaLaunchActions } from './common/pwa-launch-actions';
import { ErrorBoundary } from './components/feedback/error-boundary';
import { NoteSaveFailedBanner } from './components/navigation/note-save-failed-banner';
import { AppDialogs } from './dialogs/app-dialogs';
import { PwaOpenInAppPrompt } from './dialogs/pwa-open-in-app-prompt';
import { AppSidebar } from './layout/app-sidebar';
import {
  PageAsset,
  PageEditor,
  PageNativeFsRecovery,
  PageNotFound,
  PageSettings,
  PageWelcome,
  PageWsGraph,
  PageWsHome,
} from './pages';
import { ReaderPage } from './reader/reader-page';
import { SaveProtection } from './save-protection';
import { CloudChrome } from './share/cloud-chrome';

// Re-exported for the bootstrap entry: launch params must be consumed
// before the router captures `window.location.search`, or every later
// navigation re-emits (and a reload replays) the consumed shortcut or
// deep link.
export { consumePwaLaunchParams } from './common/pwa-install';

export function resolvePublicShell(
  pathname: string,
): React.ReactElement | null {
  // PocketBase mail links land on `/_/#/auth/confirm-password-reset/...`
  if (
    pathname === '/login' ||
    pathname.startsWith('/login/') ||
    pathname === '/_' ||
    pathname.startsWith('/_/') ||
    Boolean(pocketBaseResetTokenFromHash())
  ) {
    return <LoginPage />;
  }
  if (pathname === '/auth/callback' || pathname.startsWith('/auth/callback')) {
    return <AuthCallbackPage />;
  }
  const joinMatch = pathname.match(/^\/join\/([^/]+)\/?$/);
  if (joinMatch?.[1]) {
    return <JoinPage code={decodeURIComponent(joinMatch[1])} />;
  }
  const readerToken = matchReaderRouteToken(pathname);
  if (readerToken) {
    return <ReaderPage token={decodeURIComponent(readerToken)} />;
  }
  return null;
}

export function App({
  logger,
  store,
  rootEmitter,
  services,
}: {
  logger: Logger;
  store: Store;
  rootEmitter: RootEmitter;
  services: Services;
}) {
  const publicShell = resolvePublicShell(window.location.pathname);
  if (publicShell) {
    // Public shells mount before any app route, but they still need the app's
    // store and services: the workbench service seeds the reader's Dark
    // default into the shared color-theme preference, and only a write seen
    // by this store triggers the effect that applies `data-theme` and the
    // light/dark scheme class.
    return (
      <Provider store={store}>
        <CoreServiceProvider services={services.core}>
          {publicShell}
        </CoreServiceProvider>
      </Provider>
    );
  }

  return (
    <LoggerProvider logger={logger}>
      <Provider store={store}>
        <CoreServiceProvider services={services.core}>
          <ErrorBoundary>
            <BrowserAppDocumentSetup />
            <CloudChrome />
            <AppDialogs />
            <PwaOpenInAppPrompt />
            <PwaLaunchActions />
            <OmniSearch />
            <Toaster position="top-right" />
            <AppErrorHandler rootEmitter={rootEmitter} />
            <SaveProtection />
            <AppSidebar>
              <>
                <NoteSaveFailedBanner />
                <AppRoutes />
              </>
            </AppSidebar>
          </ErrorBoundary>
        </CoreServiceProvider>
      </Provider>
    </LoggerProvider>
  );
}

function BrowserAppDocumentSetup() {
  React.useEffect(() => {
    const abortController = new AbortController();

    syncAppDocumentTitle(document);
    initializePwaInstallPromptTracking(window);
    syncInstalledDisplayMode({
      documentRef: document,
      windowRef: window,
      signal: abortController.signal,
    });
    syncWindowControlsOverlayState({
      documentRef: document,
      navigatorRef: navigator,
      signal: abortController.signal,
    });

    return () => {
      abortController.abort();
    };
  }, []);

  return null;
}

function routeUsesWorkspaceFileTree(route: AppRouteInfo['route']): boolean {
  switch (route) {
    case 'editor':
    case 'asset':
    case 'ws-home':
    case 'ws-graph':
      return true;
    case 'welcome':
    case 'not-found':
    case 'settings-general':
    case 'settings-account':
    case 'settings-workspaces':
    case 'settings-recovery':
    case 'settings-import-export':
      return false;
    default: {
      const _exhaustiveCheck: never = route;
      throw new Error(`Unknown route: ${_exhaustiveCheck}`);
    }
  }
}

function AppRoutes() {
  const coreServices = useCoreServices();
  const { route } = useAtomValue(coreServices.navigation.$routeInfo);
  const fileTreeListState = useAtomValue(
    coreServices.workspaceState.$fileTreeListState,
  );

  if (
    fileTreeListState.status === 'native-fs-directory-not-found' &&
    routeUsesWorkspaceFileTree(route)
  ) {
    return <PageNativeFsRecovery wsName={fileTreeListState.wsName} />;
  }

  switch (route) {
    case 'editor':
      return <PageEditor />;
    case 'asset':
      return <PageAsset />;
    case 'ws-home':
      return <PageWsHome />;
    case 'ws-graph':
      return <PageWsGraph />;
    case 'welcome':
      return <PageWelcome />;
    case 'not-found':
      return <PageNotFound />;
    case 'settings-general':
    case 'settings-account':
    case 'settings-workspaces':
    case 'settings-recovery':
    case 'settings-import-export':
      return <PageSettings />;

    default: {
      // Use assertion for exhaustiveness check
      const _exhaustiveCheck: never = route;
      throw new Error(`Unknown route: ${_exhaustiveCheck}`);
    }
  }
}
